import { Card, Text } from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { Children, Fragment, useMemo } from 'react';
import type { IServiceOrganizationSettings } from '@common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { ServiceCode } from '@common/enums/serviceCode';
import { never } from '@common/utils/never';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { IOrganization } from '../../../interfaces/organization';
import type { SettingsKey } from './serviceOrganizationSettingsComponentsList';
import { ServiceOrganizationSettingFormMode } from '../../organizations/serviceSettings/ServiceOrganizationSetting';
import { ServiceOrganizationSettingInAddCarePath } from '../../organizations/serviceSettings/serviceOrganizationSetting/ServiceOrganizationSettingInAddCarePath';
import { DiagnosticEquipmentFormSection } from '../../organizations/serviceSettings/ServiceOrganizationSettingFormSections/DiagnosticEquipmentFormSection';
import { getServiceOrganizationRoles } from '../../organizations/serviceSettings/utils/serviceOrganizationRolesUtils';
import { EmailNotifications } from './components/EmailNotifications';
import { EmbloomQuestionnaires } from './components/EmbloomQuestionnaires';
import { KsyosHolterAnalystOrganization } from './components/KsyosHolterAnalystOrganization';
import { PreferredServiceOrganizationConsumers } from './components/PreferredServiceOrganizationConsumers';
import { RecipientIsOwnOrganizationSetting } from './components/RecipientIsOwnOrganizationSetting';
import { RequestRecipient } from './components/RequestRecipient';
import { ServiceGraderPoolSetings } from './components/ServiceGraderPool';
import { ServiceGroupSetting } from './components/ServiceGroupSetting';
import { Software } from './components/Software';
import { SomnologyCanReferPatientWithoutSmartphone } from './components/SomnologyCanReferPatientWithoutSmartphone';
import { SomnologyOrganization } from './components/SomnologyOrganization';
import { WaitingTimes } from './components/WaitingTimes';
import { ZorgMail } from './components/ZorgMail';
import { serviceOrganizationSettingsComponentsList } from './serviceOrganizationSettingsComponentsList';

interface IServiceOrganizationSettingsProps {
    errors: ValidationErrors;
    getErrorKey(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings | ''
    ): string | undefined;
    getServiceIndex(serviceOrganizationSettings: IServiceOrganizationSettings): number | undefined;
    hasServiceProviderAndServiceConsumerForOsasv: boolean;
    onChange(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings,
        value: IServiceOrganizationSettings[keyof RealServiceOrganizationSettings]
    ): void;
    onEmbloomQuestionnairesChange(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        selectedEmbloomQuestionnaires: string[]
    ): void;
    onValidateOne(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings
    ): void;
    onValidateTxcsId?(key: string): void;
    organization: IOrganization | undefined;
    organizationIsRequestRecipientForSubsequentService?: boolean;
    organizationServiceProvidersSettings: IOrganizationServiceProvidersSettings;
    serviceOrganizationSettings: IServiceOrganizationSettings;
    serviceProviderIsRequestRecipient?: boolean;
}

export interface IOrganizationServiceProvidersSettings {
    [serviceCode: string]: {
        isBiometrician?: boolean;
        isRequestRecipient: boolean;
        isServiceConsumerSelector?: boolean;
    };
}

export type RealServiceOrganizationSettings = Omit<IServiceOrganizationSettings, 'embloomQuestionnaires'>;

export function ServiceOrganizationSettings(props: IServiceOrganizationSettingsProps) {
    const componentsList = createComponentList(
        serviceOrganizationSettingsComponentsList(props.serviceOrganizationSettings.service.serviceType)
    );
    assertHasAuthorization([ManagerAuthorization.organizationAddEdit, ManagerAuthorization.employmentSettingsEdit]);

    const serviceOrganizationRoles = useMemo(
        () => getServiceOrganizationRoles(props.serviceOrganizationSettings.service.code),
        [props.serviceOrganizationSettings.service.code]
    );

    if (!isEmpty(serviceOrganizationRoles)) {
        return (
            <ServiceOrganizationSettingInAddCarePath serviceOrganizationSetting={props.serviceOrganizationSettings} />
        );
    }

    return (
        <Card key={`${props.serviceOrganizationSettings.serviceId}`}>
            <Card.Header title={props.serviceOrganizationSettings.service.name} />
            <Card.Body>{renderComponentList(componentsList)}</Card.Body>
        </Card>
    );

    function renderComponentList(elements: (React.ReactChild | React.ReactFragment | React.ReactPortal)[]) {
        if (elements.length === 0) {
            return (
                <Text key={`${props.serviceOrganizationSettings.serviceId}-emptyList`}>
                    {translateKey('carePath.noServiceOrganizationSettings')}
                </Text>
            );
        }

        return elements;
    }

    function createComponentList(settingsKeys: SettingsKey[] | undefined) {
        if (!settingsKeys) {
            return Children.toArray([]);
        }

        return Children.toArray(
            settingsKeys
                .map((settingsKey) => renderComponent(settingsKey, props.serviceOrganizationSettings))
                .map((component) => {
                    if (component && component.component) {
                        return <Fragment key={component.key}>{component.component}</Fragment>;
                    }
                })
        ).filter(Boolean);
    }

    function renderComponent(settingsKey: SettingsKey, serviceOrganizationSettings: IServiceOrganizationSettings) {
        switch (settingsKey) {
            case 'requestRecipient':
                return renderRequestRecipient(serviceOrganizationSettings, settingsKey);
            case 'serviceGraderPool':
                return renderServiceGraderPool(serviceOrganizationSettings, settingsKey);
            case 'emailNotifications':
                return renderEmailNotifications(serviceOrganizationSettings, settingsKey);
            case 'ksyosHolterAnalystOrganization':
                return renderKsyosHolterAnalystOrganization(serviceOrganizationSettings, settingsKey);
            case 'zorgMail':
                return renderZorgMail(serviceOrganizationSettings, settingsKey);
            case 'waitingTimes':
                return renderWaitingTimes(serviceOrganizationSettings, settingsKey);
            case 'somnologyOrganization':
                return renderSomnologyOrganization(serviceOrganizationSettings, settingsKey);
            case 'somnologyCanReferPatientWithoutSmartphone':
                return renderSomnologyCanReferPatientWithoutSmartphone(serviceOrganizationSettings, settingsKey);
            case 'software':
                return renderSoftware(serviceOrganizationSettings, settingsKey);
            case 'embloomQuestionnaires':
                return renderEmbloomQuestionnaires(serviceOrganizationSettings, settingsKey);
            case 'serviceGroupSetting':
                return renderServiceGroupSetting(serviceOrganizationSettings, settingsKey);
            case 'preferredServiceOrganizationConsumers':
                return renderPreferredServiceOrganizationConsumers(serviceOrganizationSettings, settingsKey);
            case 'referral':
                return renderReferral(serviceOrganizationSettings, settingsKey);
            case 'diagnosticEquipment':
                return renderDiagnosticEquipment(serviceOrganizationSettings, settingsKey);
            default:
                return never(settingsKey);
        }
    }

    function renderPreferredServiceOrganizationConsumers(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        if (
            !props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]
                ?.isServiceConsumerSelector
        ) {
            return null;
        }
        const preferredServiceOrganizationConsumers = PreferredServiceOrganizationConsumers({
            service: serviceOrganizationSettings.service,
            selectedConsumerOrganizationIds: serviceOrganizationSettings.preferredServiceOrganizationConsumers ?? [],
            hasError: getError(serviceOrganizationSettings, 'preferredServiceOrganizationConsumers') !== undefined,
            onChange: (value) =>
                props.onChange(serviceOrganizationSettings, 'preferredServiceOrganizationConsumers', value),
            onDoValidate: () =>
                props.onValidateOne(serviceOrganizationSettings, 'preferredServiceOrganizationConsumers'),
        });

        return {
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
            component: preferredServiceOrganizationConsumers,
        };
    }

    function renderRequestRecipient(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        let isRequestRecipientServiceProviderAvailable: boolean | undefined = undefined;
        if (props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]) {
            isRequestRecipientServiceProviderAvailable =
                props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code].isRequestRecipient;
        }

        const organizationIsRequestRecipientForSubsequentService =
            props.organizationIsRequestRecipientForSubsequentService;

        const requestRecipient = RequestRecipient({
            service: serviceOrganizationSettings.service,
            isRequestRecipient: serviceOrganizationSettings.isRequestRecipient,
            recipientIsOwnOrganization: serviceOrganizationSettings.recipientIsOwnOrganization,
            serviceProviderIsRequestRecipient: Boolean(
                isRequestRecipientServiceProviderAvailable || props.serviceProviderIsRequestRecipient
            ),
            error: getErrorMessage(serviceOrganizationSettings, 'isRequestRecipient'),
            onChange: (value) => props.onChange(serviceOrganizationSettings, 'isRequestRecipient', value),
            onDoValidate: () => props.onValidateOne(serviceOrganizationSettings, 'isRequestRecipient'),
            organizationIsRequestRecipientForSubsequentService: organizationIsRequestRecipientForSubsequentService,
        });

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: requestRecipient };
    }

    function renderServiceGraderPool(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        let isBiometricianServiceProviderAvailable: boolean | undefined = undefined;
        if (props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]) {
            isBiometricianServiceProviderAvailable =
                props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code].isBiometrician;
        }

        if (!isBiometricianServiceProviderAvailable) {
            return null;
        }

        const serviceGraderPoolSetings = ServiceGraderPoolSetings({
            service: serviceOrganizationSettings.service,
            serviceGraderPoolId: serviceOrganizationSettings.serviceGraderPoolId,
            error: getError(serviceOrganizationSettings, 'serviceGraderPoolId'),
            onChange: (value) => props.onChange(serviceOrganizationSettings, 'serviceGraderPoolId', value),
            onDoValidate: () => props.onValidateOne(serviceOrganizationSettings, 'sendNotificationOrganizationEmail'),
        });

        return {
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
            component: serviceGraderPoolSetings,
        };
    }

    function renderEmailNotifications(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        const emailNotifications = EmailNotifications({
            service: serviceOrganizationSettings.service,
            organizationEmailAddressRequestInbox: props.organization?.emailAddressRequestInbox,
            emailNotifications: { ...serviceOrganizationSettings },
            errors: props.errors,
            errorPrefix: props.getErrorKey(serviceOrganizationSettings, ''),
            onChange: (key, value) => props.onChange(serviceOrganizationSettings, key, value),
            onDoValidate: (key) => props.onValidateOne(serviceOrganizationSettings, key),
        });

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: emailNotifications };
    }

    function renderKsyosHolterAnalystOrganization(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        let isBiometricianServiceProviderAvailable: boolean | undefined = undefined;
        if (props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]) {
            isBiometricianServiceProviderAvailable =
                props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code].isBiometrician;
        }

        if (!isBiometricianServiceProviderAvailable) {
            return null;
        }

        const ksyosHolterAnalystOrganization = KsyosHolterAnalystOrganization({
            error: getError(serviceOrganizationSettings, 'ksyosHolterAnalystOrganizationId'),
            ksyosHolterAnalystOrganizationId: serviceOrganizationSettings.ksyosHolterAnalystOrganizationId ?? undefined,
            onChange: (organizationId) =>
                props.onChange(serviceOrganizationSettings, 'ksyosHolterAnalystOrganizationId', organizationId),
            onDoValidate: () => props.onValidateOne(serviceOrganizationSettings, 'ksyosHolterAnalystOrganizationId'),
            service: serviceOrganizationSettings.service,
        });

        return {
            component: ksyosHolterAnalystOrganization,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderZorgMail(serviceOrganizationSettings: IServiceOrganizationSettings, settingsKey: SettingsKey) {
        const serviceProviderAvailable = Boolean(
            props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]
        );
        if (!serviceProviderAvailable) {
            return null;
        }

        const zorgMail = ZorgMail({
            service: serviceOrganizationSettings.service,
            zorgMail: { ...serviceOrganizationSettings },
            errors: props.errors,
            errorPrefix: props.getErrorKey(serviceOrganizationSettings, ''),
            onChange: (key, value) => props.onChange(serviceOrganizationSettings, key, value),
            onDoValidate: (key) => props.onValidateOne(serviceOrganizationSettings, key),
        });

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: zorgMail };
    }

    function renderWaitingTimes(serviceOrganizationSettings: IServiceOrganizationSettings, settingsKey: SettingsKey) {
        const waitingTimes = WaitingTimes({
            waitingTimes: { ...serviceOrganizationSettings },
            errors: props.errors,
            errorPrefix: props.getErrorKey(serviceOrganizationSettings, ''),
            onChange: (key, value) => props.onChange(serviceOrganizationSettings, key, value),
            onDoValidate: (key) => props.onValidateOne(serviceOrganizationSettings, key),
        });

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: waitingTimes };
    }

    function renderSomnologyOrganization(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        const serviceProviderAvailable = Boolean(
            props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]
        );

        if (!serviceProviderAvailable) {
            return null;
        }

        const somnologyOrganization = SomnologyOrganization({
            service: serviceOrganizationSettings.service,
            somnologyOrganizationType: serviceOrganizationSettings.somnologyOrganizationType,
            error: getError(serviceOrganizationSettings, 'somnologyOrganizationType'),
            onChange: (value) => props.onChange(serviceOrganizationSettings, 'somnologyOrganizationType', value),
            onDoValidate: () => props.onValidateOne(serviceOrganizationSettings, 'somnologyOrganizationType'),
        });

        return {
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
            component: somnologyOrganization,
        };
    }

    function renderSomnologyCanReferPatientWithoutSmartphone(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        if (serviceOrganizationSettings.service.code !== ServiceCode.OSASV) {
            return null;
        }

        const somnologyCanReferPatientWithoutSmartphone = SomnologyCanReferPatientWithoutSmartphone({
            service: serviceOrganizationSettings.service,
            canReferPatientWithoutSmartphone: serviceOrganizationSettings.canReferPatientWithoutSmartphone,
            error: getError(serviceOrganizationSettings, 'canReferPatientWithoutSmartphone'),
            onChange: (value) => props.onChange(serviceOrganizationSettings, 'canReferPatientWithoutSmartphone', value),
            onDoValidate: () => props.onValidateOne(serviceOrganizationSettings, 'canReferPatientWithoutSmartphone'),
        });

        return {
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
            component: somnologyCanReferPatientWithoutSmartphone,
        };
    }

    function renderSoftware(serviceOrganizationSettings: IServiceOrganizationSettings, settingsKey: SettingsKey) {
        let isBiometricianServiceProviderAvailable: boolean | undefined = undefined;
        let serviceProviderAvailable = false;
        if (props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code]) {
            serviceProviderAvailable = true;
            isBiometricianServiceProviderAvailable =
                props.organizationServiceProvidersSettings[serviceOrganizationSettings.service.code].isBiometrician;
        }

        const showSendBittiumRecordingsTo =
            serviceOrganizationSettings.service.code === ServiceCode.TCOH &&
            isBiometricianServiceProviderAvailable === true;
        const showOptoConnectPracticeId =
            serviceOrganizationSettings.service.code === ServiceCode.TOO && serviceProviderAvailable;

        if (!showSendBittiumRecordingsTo && !showOptoConnectPracticeId) {
            return null;
        }

        const software = Software({
            software: { ...serviceOrganizationSettings },
            showSendBittiumRecordingsTo: showSendBittiumRecordingsTo,
            showOptoConnectPracticeId: showOptoConnectPracticeId,
            errors: props.errors,
            errorPrefix: props.getErrorKey(serviceOrganizationSettings, ''),
            onChange: (key, value) => props.onChange(serviceOrganizationSettings, key, value),
            onDoValidate: (key) => props.onValidateOne(serviceOrganizationSettings, key),
        });

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: software };
    }

    function renderEmbloomQuestionnaires(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        const embloomQuestionnaires = EmbloomQuestionnaires({
            service: serviceOrganizationSettings.service,
            selectedEmbloomQuestionnaires: serviceOrganizationSettings.embloomQuestionnaires,
            onChange: (selectedQuestionnaires) =>
                props.onEmbloomQuestionnairesChange(serviceOrganizationSettings, selectedQuestionnaires),
        });

        return {
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
            component: embloomQuestionnaires,
        };
    }

    function renderDiagnosticEquipment(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        const diagnosticEquipment = DiagnosticEquipmentFormSection({
            serviceCode: serviceOrganizationSettings.service.code,
            mode: ServiceOrganizationSettingFormMode.edit,
            canPerformGlaucoomExamination: serviceOrganizationSettings.canPerformGlaucoomExamination,
            handleChange: (value) =>
                props.onChange(serviceOrganizationSettings, 'canPerformGlaucoomExamination', value),
        });

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: diagnosticEquipment };
    }

    function renderServiceGroupSetting(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        settingsKey: SettingsKey
    ) {
        const serviceGroupSettings = ServiceGroupSetting({
            service: serviceOrganizationSettings.service,
            serviceGroupSettingId: serviceOrganizationSettings.serviceGroupSettingId,
            error: getError(serviceOrganizationSettings, 'serviceGroupSettingId'),
            onChange: (value) => props.onChange(serviceOrganizationSettings, 'serviceGroupSettingId', value),
            onDoValidate: async () => props.onValidateOne(serviceOrganizationSettings, 'serviceGroupSettingId'),
        });

        if (!serviceGroupSettings) {
            return null;
        }

        return { key: generateComponentKey(serviceOrganizationSettings, settingsKey), component: serviceGroupSettings };
    }

    function renderReferral(serviceOrganizationSettings: IServiceOrganizationSettings, settingsKey: SettingsKey) {
        if (!props.hasServiceProviderAndServiceConsumerForOsasv) {
            return null;
        }

        const recipientIsOwnOrganizationSetting = RecipientIsOwnOrganizationSetting({
            service: serviceOrganizationSettings.service,
            isRecipientIsOwnOrganization: serviceOrganizationSettings.recipientIsOwnOrganization,
            error: getError(serviceOrganizationSettings, 'recipientIsOwnOrganization'),
            onChange: (value) => props.onChange(serviceOrganizationSettings, 'recipientIsOwnOrganization', value),
            onDoValidate: async () => props.onValidateOne(serviceOrganizationSettings, 'recipientIsOwnOrganization'),
        });

        if (!recipientIsOwnOrganizationSetting) {
            return null;
        }

        return {
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
            component: recipientIsOwnOrganizationSetting,
        };
    }

    function generateComponentKey(serviceOrganizationSettings: IServiceOrganizationSettings, key: SettingsKey) {
        return `${key}-${serviceOrganizationSettings.service.code}`;
    }

    function getError(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings
    ) {
        let errorKey = props.getErrorKey(serviceOrganizationSettings, key);

        const error = errorKey ? props.errors?.get(errorKey) : undefined;
        if (error) {
            return error;
        }

        errorKey = getAddSchemaErrorKey(serviceOrganizationSettings, key);

        return errorKey ? props.errors?.get(errorKey) : undefined;
    }

    function getErrorMessage(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings
    ) {
        const error = getError(serviceOrganizationSettings, key);

        return error ? getValidationErrorTranslation(error) : undefined;
    }

    function getAddSchemaErrorKey(serviceOrganizationSettings: IServiceOrganizationSettings, key: string) {
        const serviceIndex = props.getServiceIndex(serviceOrganizationSettings);
        if (serviceIndex === undefined || serviceIndex === -1) {
            return undefined;
        }

        return `services.${serviceIndex}.serviceOrganizationSettings.${key}`;
    }
}
